import * as React from 'react';
import {
  VictoryChart,
  VictoryBar,
  VictoryContainer,
  VictoryAxis,
} from 'victory';
import { assoc, assocPath, unless, always } from 'ramda';
import { number, string, func, arrayOf, bool, oneOf, shape } from 'prop-types';

import GenericBar from './GenericBar';
import GenericBarLabel from './GenericBarLabel';
import { victoryTheme } from '../../theme';
import { chartPadding } from './GenericBarChart';
import { HoveredDate as IHoveredDate } from '../../models';

export const TextAnchorType = oneOf(['start', 'middle', 'end']);
export const ChartDataType = arrayOf(
  shape({
    x: number.isRequired,
    y: number.isRequired,
    width: number.isRequired,
  }),
);

export const GenericBarChartCore = ({
  width,
  height,
  xTickLabels,
  xTickValues,
  labelAnchor,
  maxY,
  chartData,
  showVerticalGrid,
  onMouseOver,
  onMouseOut,
  onClick,
  selectedId,
  hoveredId,
  hoveredDate,
  hoverHPadding,
  ...restProps
}) => {
  return (
    <VictoryChart
      containerComponent={<VictoryContainer responsive={false} />}
      padding={{ ...chartPadding, left: 0 }}
      width={width + chartPadding.right}
      height={height}
    >
      <VictoryAxis
        dependentAxis
        domain={[0, maxY]}
        style={victoryTheme.commonAxis}
      />
      <VictoryBar
        data={chartData}
        cornerRadius={1}
        style={{
          data: {
            fill: ({ fill }) => fill,
            width: ({ width }) => width,
          },
        }}
        dataComponent={
          <GenericBar
            onMouseOver={(datum, { nativeEvent }) => {
              onMouseOver(datum, restProps, [
                nativeEvent.offsetX,
                nativeEvent.offsetY,
              ]);
            }}
            onMouseOut={datum => {
              onMouseOut(datum, restProps);
            }}
            onClick={datum => {
              onClick(datum, restProps);
            }}
            hoveredId={hoveredId}
            hoveredDate={hoveredDate}
            selectedId={selectedId}
            hoverHPadding={hoverHPadding}
            topPadding={chartPadding.top}
          />
        }
      />
      <VictoryAxis
        style={unless(
          always(showVerticalGrid),
          assoc('grid', { stroke: 'transparent', pointerEvents: 'none' }),
          assocPath(
            ['axis', 'stroke'],
            victoryTheme.commonAxis.grid.stroke,
            victoryTheme.commonAxis,
          ),
        )}
        offsetY={chartPadding.bottom}
        tickLabelComponent={
          <GenericBarLabel textAnchor={labelAnchor} xTickValues={xTickValues} />
        }
        tickFormat={value => xTickLabels[xTickValues.indexOf(value)]}
        tickValues={xTickValues}
        domain={[0, width]}
      />
    </VictoryChart>
  );
};

GenericBarChartCore.propTypes = {
  width: number.isRequired,
  height: number.isRequired,
  xTickLabels: arrayOf(string).isRequired,
  xTickValues: arrayOf(number).isRequired,
  labelAnchor: TextAnchorType.isRequired,
  maxY: number.isRequired,
  chartData: ChartDataType.isRequired,
  showVerticalGrid: bool.isRequired,
  onMouseOver: func.isRequired,
  onMouseOut: func.isRequired,
  onClick: func.isRequired,
  selectedId: string,
  hoveredId: string,
  hoveredDate: IHoveredDate,
  hoverHPadding: number.isRequired,
};

export default GenericBarChartCore;



// WEBPACK FOOTER //
// ./src/components/GenericBarChart/GenericBarChartCore.jsx